import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

export default function AddVehicle() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    license_plate: '',
    vin: '',
    current_mileage: '',
    last_service_date: '',
    last_oil_type: ''
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);

  useEffect(() => {
    if (isEdit) {
      fetchVehicle();
    }
  }, [id]);

  const fetchVehicle = async () => {
    try {
      const response = await axios.get(`${API}/vehicles/${id}`);
      setFormData({
        make: response.data.make,
        model: response.data.model,
        year: response.data.year,
        license_plate: response.data.license_plate,
        vin: response.data.vin || '',
        current_mileage: response.data.current_mileage || '',
        last_service_date: response.data.last_service_date || '',
        last_oil_type: response.data.last_oil_type || ''
      });
    } catch (error) {
      toast.error('Failed to load vehicle');
      navigate('/dashboard');
    } finally {
      setFetching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate oil type is selected
    if (!formData.last_oil_type) {
      toast.error('Please select the engine oil type used in last service');
      return;
    }
    
    setLoading(true);

    try {
      const payload = {
        ...formData,
        vin: formData.vin || null,
        current_mileage: formData.current_mileage ? parseInt(formData.current_mileage) : null,
        last_service_date: formData.last_service_date || null,
        last_oil_type: formData.last_oil_type || null
      };

      if (isEdit) {
        await axios.put(`${API}/vehicles/${id}`, payload);
        toast.success('Vehicle updated successfully');
      } else {
        await axios.post(`${API}/vehicles`, payload);
        toast.success('Vehicle added successfully');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save vehicle');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-xl font-heading text-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_vehicle-maintenance-21/artifacts/76wdsgvy_WhatsApp%20Image%202025-12-16%20at%2017.28.15.jpeg" 
                alt="Logo" 
                className="h-12 w-12 object-contain"
              />
              <span className="text-xl font-heading font-bold text-foreground">EXCEL ERA</span>
            </div>
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard-btn"
              className="font-bold uppercase text-sm"
            >
              ← Back to Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white border-2 border-border rounded-sm shadow-hard p-8">
          <h1 className="text-3xl font-heading font-bold text-foreground mb-8">
            {isEdit ? 'Edit Vehicle' : 'Add New Vehicle'}
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="make" className="font-bold uppercase text-sm tracking-wide">Make *</Label>
                <Input
                  id="make"
                  required
                  data-testid="vehicle-make-input"
                  className="input-field mt-2"
                  placeholder="e.g., Toyota"
                  value={formData.make}
                  onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="model" className="font-bold uppercase text-sm tracking-wide">Model *</Label>
                <Input
                  id="model"
                  required
                  data-testid="vehicle-model-input"
                  className="input-field mt-2"
                  placeholder="e.g., Camry"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="year" className="font-bold uppercase text-sm tracking-wide">Year *</Label>
                <Input
                  id="year"
                  type="number"
                  required
                  data-testid="vehicle-year-input"
                  className="input-field mt-2"
                  min="1900"
                  max={new Date().getFullYear() + 1}
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                />
              </div>

              <div>
                <Label htmlFor="license_plate" className="font-bold uppercase text-sm tracking-wide">License Plate *</Label>
                <Input
                  id="license_plate"
                  required
                  data-testid="vehicle-license-input"
                  className="input-field mt-2 font-mono"
                  placeholder="ABC-123-XY"
                  value={formData.license_plate}
                  onChange={(e) => setFormData({ ...formData, license_plate: e.target.value.toUpperCase() })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="vin" className="font-bold uppercase text-sm tracking-wide">VIN (Optional)</Label>
              <Input
                id="vin"
                data-testid="vehicle-vin-input"
                className="input-field mt-2 font-mono"
                placeholder="Vehicle Identification Number"
                value={formData.vin}
                onChange={(e) => setFormData({ ...formData, vin: e.target.value.toUpperCase() })}
              />
            </div>

            <div className="bg-blue-50 border-2 border-blue-200 p-4 rounded-sm">
              <Label className="font-bold uppercase text-sm tracking-wide text-blue-800 mb-3 block">
                Current Mileage (Recommended)
              </Label>
              <Input
                id="current_mileage"
                type="number"
                data-testid="vehicle-mileage-input"
                className="input-field"
                placeholder="e.g., 50000"
                value={formData.current_mileage}
                onChange={(e) => setFormData({ ...formData, current_mileage: e.target.value })}
              />
              <p className="text-sm text-blue-700 mt-2">
                Enter your current odometer reading in kilometers. This helps track service intervals accurately.
              </p>
            </div>

            <div className="bg-green-50 border-2 border-green-200 p-4 rounded-sm">
              <Label className="font-bold uppercase text-sm tracking-wide text-green-800 mb-3 block">
                Last Oil Type Used *
              </Label>
              <Select 
                value={formData.last_oil_type} 
                onValueChange={(v) => setFormData({ ...formData, last_oil_type: v })}
              >
                <SelectTrigger className="input-field" data-testid="vehicle-oil-type-select">
                  <SelectValue placeholder="Select oil type from last service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Synthetic Oil">Synthetic Oil (Next service: 6,000km / 5 months)</SelectItem>
                  <SelectItem value="Semi-Synthetic Oil">Semi-Synthetic Oil (Next service: 4,000km / 4 months)</SelectItem>
                  <SelectItem value="Mineral Oil">Mineral Oil (Next service: 3,000km / 2 months)</SelectItem>
                  <SelectItem value="High-Mileage Mineral Oil">High-Mileage Mineral Oil (Next service: 4,000km / 4 months)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-green-700 mt-2">
                This is required to calculate your next service date accurately.
              </p>
            </div>

            <div>
              <Label htmlFor="last_service_date" className="font-bold uppercase text-sm tracking-wide">Last Service Date (Optional)</Label>
              <Input
                id="last_service_date"
                type="date"
                data-testid="vehicle-last-service-input"
                className="input-field mt-2"
                value={formData.last_service_date}
                onChange={(e) => setFormData({ ...formData, last_service_date: e.target.value })}
              />
              <p className="text-sm text-muted-foreground mt-2">
                If provided, next service date will be calculated based on the oil type selected above.
              </p>
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                disabled={loading}
                data-testid="vehicle-submit-btn"
                className="btn-primary flex-1 h-12 text-lg"
              >
                {loading ? 'Saving...' : (isEdit ? 'Update Vehicle' : 'Add Vehicle')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/dashboard')}
                data-testid="vehicle-cancel-btn"
                className="btn-secondary flex-1 h-12 text-lg"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}